/**
 * Console navigation.
 *
 * Pages come from docs/design/07-frontend.md; the grouping is what an operator
 * reaches for during an incident, not the order the docs list them in.
 *
 * Content and Developer were one "Tools" group of eight - over a third of the
 * sidebar under one heading - which put what this instance has collected
 * (library, downloads, transcripts, watchlist) in a list with the pages for
 * building against its API, and made the group the one place a reader had to
 * scan rather than glance.
 */

export const NAV_GROUPS = ['bd', 'monitor', 'pool', 'content', 'developer', 'access', 'operations'] as const
export type NavGroup = (typeof NAV_GROUPS)[number]

export type NavIconName =
  | 'gauge'
  | 'idCard'
  | 'globe'
  | 'key'
  | 'terminal'
  | 'plug'
  | 'link'
  | 'book'
  | 'list'
  | 'sliders'
  | 'bell'
  | 'server'
  | 'activity'
  | 'archive'
  | 'download'
  | 'clock'
  | 'users'
  | 'lock'
  | 'blocks'
  | 'captions'
  | 'search'

export interface NavItem {
  path: string
  /** Key in the console namespace. */
  labelKey: string
  group: NavGroup
  icon: NavIconName
  /**
   * Shown to the public demo account.
   *
   * Absent means no, which is the only safe default: a page added later is
   * hidden from a demo instance until somebody decides otherwise, rather than
   * being published to strangers because nobody remembered this field.
   *
   * The flag hides a link; it does not protect anything. The API refuses these
   * pages' endpoints for a demo caller on its own - see `demo_read` and
   * `read_admin_demo` in the backend - and this exists so a demo visitor sees a
   * console that works instead of a sidebar two thirds of which answers 403.
   */
  demo?: true
  /**
   * TK patch 06: the BD tools section, always visible under its own heading
   * between the primary block and "More".
   */
  bd?: true
  /**
   * TK redesign v2: shown in the always-visible top block of the sidebar.
   * Everything else sits under the collapsible "More" section.
   */
  primary?: true
}

export const NAV_ITEMS: readonly NavItem[] = [
  { path: '/', labelKey: 'nav.overview', group: 'monitor', icon: 'gauge', demo: true, primary: true },
  // TK patch 06/07: BD tools (TK BD API via /bd-console). Shown only to accounts with BD tools permission.
  { path: '/bd-tools/screener', labelKey: 'nav.bdScreener', group: 'bd', icon: 'users', bd: true },
  { path: '/bd-tools/discover', labelKey: 'nav.bdDiscover', group: 'bd', icon: 'search', bd: true },
  { path: '/bd-tools/campaigns', labelKey: 'nav.bdCampaigns', group: 'bd', icon: 'activity', bd: true },
  { path: '/identities', labelKey: 'nav.identities', group: 'pool', icon: 'idCard', primary: true },
  { path: '/proxies', labelKey: 'nav.proxies', group: 'pool', icon: 'globe', primary: true },
  { path: '/scheduler', labelKey: 'nav.scheduler', group: 'pool', icon: 'sliders', demo: true },
  { path: '/library', labelKey: 'nav.library', group: 'content', icon: 'archive', demo: true },
  { path: '/downloads', labelKey: 'nav.downloads', group: 'content', icon: 'download', demo: true },
  { path: '/transcriptions', labelKey: 'nav.transcriptions', group: 'content', icon: 'captions' },
  { path: '/watchlist', labelKey: 'nav.watchlist', group: 'content', icon: 'clock' },
  { path: '/playground', labelKey: 'nav.playground', group: 'developer', icon: 'terminal', demo: true, primary: true },
  { path: '/api-guide', labelKey: 'nav.apiGuide', group: 'developer', icon: 'link', demo: true, primary: true },
  { path: '/tools', labelKey: 'nav.tools', group: 'developer', icon: 'blocks', demo: true },
  { path: '/docs', labelKey: 'nav.apiDocs', group: 'developer', icon: 'book', demo: true },
  // Not '/mcp': that path is the MCP endpoint itself, mounted on the API.
  { path: '/mcp-guide', labelKey: 'nav.mcp', group: 'developer', icon: 'plug', demo: true },
  { path: '/api-keys', labelKey: 'nav.apiKeys', group: 'access', icon: 'key', demo: true, primary: true },
  { path: '/endpoint-access', labelKey: 'nav.endpointAccess', group: 'access', icon: 'lock' },
  { path: '/users', labelKey: 'nav.users', group: 'access', icon: 'users' },
  { path: '/logs', labelKey: 'nav.logs', group: 'operations', icon: 'list', demo: true, primary: true },
  { path: '/system', labelKey: 'nav.system', group: 'operations', icon: 'server', demo: true },
  { path: '/diagnose', labelKey: 'nav.diagnose', group: 'operations', icon: 'activity' },
  { path: '/backup', labelKey: 'nav.backup', group: 'operations', icon: 'archive' },
  { path: '/notifications', labelKey: 'nav.notifications', group: 'operations', icon: 'bell' },
  { path: '/settings', labelKey: 'nav.settings', group: 'operations', icon: 'sliders' },
]

/**
 * The navigation a role may see.
 *
 * Only the demo account is filtered. Every other role reaches every page and is
 * refused per endpoint if it lacks the rank, which is the behaviour the console
 * has always had: an operator seeing a Users link and being told no is a useful
 * thing to learn about their own instance. A stranger on a public demo learns
 * nothing from a link that always fails, so theirs is trimmed instead.
 */
export function navItemsFor(role: string | null | undefined): readonly NavItem[] {
  return role === 'demo' ? NAV_ITEMS.filter((item) => item.demo) : NAV_ITEMS
}

/** Whether a role may open a page at all, for a directly typed URL. */
export function canOpen(role: string | null | undefined, path: string): boolean {
  if (role !== 'demo') return true
  if (PUBLIC_ROUTES.includes(path) || path === '/about') return true
  const item = navItemFor(path)
  return item?.demo === true
}

/** Routes reachable without a session. */
export const PUBLIC_ROUTES: readonly string[] = ['/login', '/setup']

/**
 * Pages that are real but not in the navigation groups.
 *
 * About is the colophon: it has its own link pinned under the nav rather than
 * a place in a group, and the breadcrumb still has to be able to name it -
 * without this it resolved to "page not found" on a page that had just
 * rendered.
 */
const UNLISTED: readonly NavItem[] = [
  { path: '/about', labelKey: 'page.about.title', group: 'operations', icon: 'server' },
]

export function navItemFor(path: string): NavItem | undefined {
  if (path === '/') return NAV_ITEMS[0]
  return [...NAV_ITEMS, ...UNLISTED].find(
    (item) => item.path !== '/' && path.startsWith(item.path),
  )
}

/**
 * TK redesign v2: the order of the always-visible block. Tasks first, because
 * "look something up" is what most visits are for.
 */
export const PRIMARY_ORDER: readonly string[] = [
  '/',
  '/playground',
  '/api-guide',
  '/proxies',
  '/identities',
  '/api-keys',
  '/logs',
]
